import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { ListToListMatchType } from "../../advanced-flow/common/condition";
import {
  numberComparisonInequalityOperators,
  numberComparisonOperators,
} from "../../advanced-flow/expressions/number-expression";

const ParcelNodeSchema = z.object({
  _id: zExt.objectId(),
  kind: z.literal("parcel"),
  parcelId: zExt.objectId(),
});
export type ParcelNode = z.infer<typeof ParcelNodeSchema>;
export enum ParcelWeightUnit {
  POUNDS = "POUNDS",
  GRAMS = "GRAMS",
}

export enum ParcelConditionKind {
  WEIGHT_OZ = "WEIGHT_OZ",
  ITEM_COUNT = "ITEM_COUNT",
  PRODUCT_TAGS = "PRODUCT_TAGS",
}

export const parcelConditionKindToLabel: Record<ParcelConditionKind, string> = {
  [ParcelConditionKind.WEIGHT_OZ]: "Weight",
  [ParcelConditionKind.ITEM_COUNT]: "Item quantity",
  [ParcelConditionKind.PRODUCT_TAGS]: "Product tags",
};

const ParcelWeightConditionSchema = z.object({
  conditionKind: z.literal(ParcelConditionKind.WEIGHT_OZ),
  matchType: createEnum(numberComparisonInequalityOperators, {
    enumName: "NumberComparisonInequalityOperators",
  }),
  matchValue: z.number(),
});
export type ParcelWeightCondition = z.infer<typeof ParcelWeightConditionSchema>;

const ParcelItemCountConditionSchema = z.object({
  conditionKind: z.literal(ParcelConditionKind.ITEM_COUNT),
  matchType: createEnum(numberComparisonOperators, {
    enumName: "NumberComparisonOperators",
  }),
  matchValue: z.number(),
});
export type ParcelItemCountCondition = z.infer<
  typeof ParcelItemCountConditionSchema
>;

const ParcelProductTagConditionSchema = z.object({
  conditionKind: z.literal(ParcelConditionKind.PRODUCT_TAGS),
  matchType: createEnum(ListToListMatchType, {
    enumName: "ListToListMatchType",
  }),
  matchValues: z.array(z.string()),
  requireExactMatch: z.boolean(),
});
export type ParcelProductTagCondition = z.infer<
  typeof ParcelProductTagConditionSchema
>;

const ParcelConditionSchema = z.discriminatedUnion("conditionKind", [
  ParcelWeightConditionSchema,
  ParcelItemCountConditionSchema,
  ParcelProductTagConditionSchema,
]);
export type ParcelCondition = z.infer<typeof ParcelConditionSchema>;

const ParcelConditionNodeSchema = z.object({
  _id: zExt.objectId(),
  kind: z.literal("condition"),
  matchingNextNode: zExt.objectId(),
  nonMatchingNextNode: zExt.objectId(),
  condition: ParcelConditionSchema,
});
export type ParcelConditionNode = z.infer<typeof ParcelConditionNodeSchema>;

const TreeNodeSchema = z.discriminatedUnion("kind", [
  ParcelNodeSchema,
  ParcelConditionNodeSchema,
]);
export type TreeNode = z.infer<typeof TreeNodeSchema>;

export const CheckoutParcelTreeSchema = z.object({
  root: zExt.objectId(),
  nodes: z.array(TreeNodeSchema),
});
export type CheckoutParcelTree = z.infer<typeof CheckoutParcelTreeSchema>;
